import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { getCurrentOrganizationId } from '@/components/App/selectors'
import ErrorConfirmation from '@/components/ErrorConfirmation'
import Page from '@/components/Page'
import { resetRequest } from '@/store/actions'
import { getConfiguration, getIdentityProviders, isRequestError, isRequestPending } from '@/store/selectors'

import { addIdentityProvider, deleteIdentityProvider, fetchSamlIdpConfig, updateIdentityProvider } from './actions'
import { ADD_IDP, DELETE_IDP, FETCH_SAML_IDP_CONFIG, UPDATE_IDP } from './actionTypes'
import CreateSamlIdentityProvider from './CreateSamlIdentityProvider'
import EditSamlIdentityProvider from './EditSamlIdentityProvider'
import messages from './messages'
import SamlConfiguration from './SamlConfiguration'
import { getUserPool } from './selectors'
import { formatAsValidCognitoName } from './utils'
import ViewSamlIdentityProvider from './ViewSamlIdentityProvider'

function OrganizationSecurityPage(props: any) {
  const intl = useIntl()

  const [state, setState] = useState({
    isEditing: false
  })

  // componentDidMount
  useEffect(() => {
    props.actions.resetRequest([ADD_IDP, UPDATE_IDP, DELETE_IDP, FETCH_SAML_IDP_CONFIG])
    props.actions.fetchSamlIdpConfig(props.organizationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addIdentityProvider = async (idp: any) => {
    idp.name = formatAsValidCognitoName(props.userPool.companyCode)
    await props.actions.addIdentityProvider(idp)
    setState({ isEditing: false })
  }

  const updateIdentityProvider = async (idp: any) => {
    await props.actions.updateIdentityProvider(props.identityProviders[0].id, idp)
    setState({ isEditing: false })
  }

  const deleteIdentityProvider = async (idpId: any) => {
    await props.actions.deleteIdentityProvider(idpId)
    setState({ isEditing: true })
  }

  const onEdit = () => {
    setState({ isEditing: true })
  }

  const onEditCancel = () => {
    setState({ isEditing: false })
  }

  function renderIdentityProvider() {
    if (props.identityProviders[0] && !state.isEditing) {
      return (
        <>
          <SamlConfiguration
            acs={props.userPool.acs}
            apiHost={props.configuration.apiHost}
            companyCode={props.userPool.companyCode}
            entityId={props.userPool.entityId}
            poolId={props.configuration.cognito.userPoolId}
          />
          <ViewSamlIdentityProvider identityProvider={props.identityProviders[0]} onEdit={onEdit} />
        </>
      )
    } else if (props.identityProviders[0] && state.isEditing) {
      return (
        <>
          <SamlConfiguration
            acs={props.userPool.acs}
            apiHost={props.configuration.apiHost}
            companyCode={props.userPool.companyCode}
            entityId={props.userPool.entityId}
            poolId={props.configuration.cognito.userPoolId}
          />
          <EditSamlIdentityProvider
            isActionInProgress={props.isActionInProgress}
            onUpdate={updateIdentityProvider}
            onDelete={deleteIdentityProvider}
            onCancel={onEditCancel}
            identityProvider={props.identityProviders[0]}
          />
        </>
      )
    } else {
      return (
        <>
          <SamlConfiguration
            acs={props.userPool.acs}
            apiHost={props.configuration.apiHost}
            companyCode={props.userPool.companyCode}
            entityId={props.userPool.entityId}
            poolId={props.configuration.cognito.userPoolId}
          />
          <CreateSamlIdentityProvider isActionInProgress={props.isActionInProgress} onCreate={addIdentityProvider} />
        </>
      )
    }
  }

  const { hasError } = props
  return (
    <Page>
      <Page.Header title={<FormattedMessage {...messages.title} />} />
      {hasError ? (
        <ErrorConfirmation
          error={intl.formatMessage(messages.errorTitle)}
          subMessage={intl.formatMessage(messages.errorMessage)}
        />
      ) : (
        renderIdentityProvider()
      )}
    </Page>
  )
}

OrganizationSecurityPage.displayName = 'OrganizationSecurityPage'

OrganizationSecurityPage.propTypes = {
  actions: PropTypes.shape({
    addIdentityProvider: PropTypes.func.isRequired,
    deleteIdentityProvider: PropTypes.func.isRequired,
    fetchSamlIdpConfig: PropTypes.func.isRequired,
    resetRequest: PropTypes.func.isRequired,
    updateIdentityProvider: PropTypes.func.isRequired
  }).isRequired,
  configuration: PropTypes.shape({
    apiHost: PropTypes.string.isRequired,
    cognito: PropTypes.shape({
      userPoolId: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  organizationId: PropTypes.string.isRequired,
  hasError: PropTypes.bool,
  identityProviders: PropTypes.array.isRequired,

  isActionInProgress: PropTypes.bool.isRequired,
  userPool: PropTypes.object
}

OrganizationSecurityPage.defaultProps = {
  hasError: false,
  identityProviders: [],
  userPool: {}
}

const DisconnectedOrganizationSecurityPage = OrganizationSecurityPage
export { /** @deprecated */ DisconnectedOrganizationSecurityPage as OrganizationSecurityPage }

function mapStateToProps(state: any) {
  return {
    configuration: getConfiguration(state),
    hasError: isRequestError(state, [ADD_IDP, UPDATE_IDP, DELETE_IDP, FETCH_SAML_IDP_CONFIG]),
    identityProviders: getIdentityProviders(state),
    isActionInProgress: isRequestPending(state, [ADD_IDP, UPDATE_IDP, DELETE_IDP, FETCH_SAML_IDP_CONFIG]),
    userPool: getUserPool(state),
    organizationId: getCurrentOrganizationId(state)
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators(
      { addIdentityProvider, updateIdentityProvider, deleteIdentityProvider, fetchSamlIdpConfig, resetRequest },
      dispatch
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DisconnectedOrganizationSecurityPage)
